import React, { useState } from 'react';
import { I18n } from 'react-redux-i18n';

import Cell from 'components/Cell';
import ContentTooltip from 'components/ContentTooltip';
import Table from 'components/Table';
import Header from 'components/Table/Header';

import {
  showDistributionKPDNBtg,
  showDistributionKPIConsoRate,
  showDistributionKPIBestVintage,
  showDistributionKPIOldDN
} from 'utils/personalisation';

const KeyIndicators = (props) => {
  const {
    tags_names = [],
    handleExpanded,
    expanded,
    data,
    form,
    hiddenColumns = []
  } = props;
  const [sortState, setSortState] = useState({
    sorted: [],
    expanded: {}
  });

  const isSpirit = form.wineType === 'spirit';
  const isOnTradeMarket = form.currentNetworkCode === 'ON';
  const shouldDisplayGlassAvgPrice = isSpirit && isOnTradeMarket;
  const shouldDisplayBottleAvgPrice = !shouldDisplayGlassAvgPrice;

  const handleSort = (id) => {
    let { sorted } = sortState;
    let desc = true;

    if (sorted.length > 0 && sorted[0].id === id) {
      desc = !sorted[0].desc;
    }

    sorted = [{ id, desc }];
    setSortState({ sorted, expanded: {} });
  };

  const columns = [
    {
      Header: () => (
        <Header
          styles={`justify-content:flex-start;padding-left:30px`}
          title={I18n.t('restaurants&caves.kpi.columns.wine')}
          sortable={true}
          sort={() => handleSort('wine')}
        />
      ),
      Cell: (props) => (
        <Cell
          {...props}
          font="sfcompact_text"
          expander={true}
          tooltip={
            props.value
              ? {
                  placement: 'top',
                  content: <ContentTooltip values={[props.value]} />
                }
              : undefined
          }
          onClick={() => handleExpanded(props.index)}
          styles={`justify-content:flex-start;padding-left:30px;font-weight: 600;`}
        >
          {props.value}
        </Cell>
      ),
      minWidth: 230,
      accessor: 'wine',
      name: I18n.t('restaurants&caves.kpi.columns.wine')
    },
    {
      Header: () => (
        <Header
          title={I18n.t('restaurants&caves.kpi.columns.dn')}
          tag={I18n.t('restaurants&caves.kpi.columns.dnShort')}
          sortable={true}
          sort={() => handleSort('dn')}
          tooltip={{
            placement: 'top',
            content: (
              <ContentTooltip values={[I18n.t('tableHeaderTooltips.dn')]} />
            )
          }}
        />
      ),
      Cell: (props) => <Cell {...props}>{props.value}</Cell>,
      accessor: 'dn',
      name: I18n.t('restaurants&caves.kpi.columns.dn')
    },
    {
      Header: () => (
        <Header
          title={I18n.t('restaurants&caves.kpi.columns.tp')}
          tag={I18n.t('restaurants&caves.kpi.columns.tpShort')}
          sortable={true}
          sort={() => handleSort('tp')}
          tooltip={{
            placement: 'top',
            content: (
              <ContentTooltip values={[I18n.t('tableHeaderTooltips.tp')]} />
            )
          }}
        />
      ),
      Cell: (props) => (
        <Cell {...props} font="sfcompact_text" styles={`font-weight: 600;`}>
          {props.value}
        </Cell>
      ),
      sortMethod: (a, b) => {
        if (a && b) {
          a = Number(a.replace('%', ''));
          b = Number(b.replace('%', ''));
          return a - b;
        }
      },
      accessor: 'tp',
      name: I18n.t('restaurants&caves.kpi.columns.tp')
    },
    {
      Header: () => (
        <Header
          title={I18n.t('restaurants&caves.kpi.columns.vm')}
          tag={I18n.t('restaurants&caves.kpi.columns.vmShort')}
          sortable={true}
          sort={() => handleSort('vm')}
          tooltip={{
            placement: 'top',
            content: (
              <ContentTooltip values={[I18n.t('tableHeaderTooltips.vm')]} />
            )
          }}
        />
      ),
      Cell: (props) => (
        <Cell
          {...props}
          value={props.value}
          styles={`border-right: 1px solid #e8e9eb`}
        />
      ),
      accessor: 'vm',
      name: I18n.t('restaurants&caves.kpi.columns.vm')
    },
    {
      Header: () => (
        <Header
          title={I18n.t('restaurants&caves.kpi.columns.lostPov')}
          sortable={true}
          sort={() => handleSort('pdv_perdus')}
          tooltip={{
            placement: 'top',
            content: (
              <ContentTooltip
                values={[I18n.t('tableHeaderTooltips.lostPov')]}
              />
            )
          }}
        />
      ),
      Cell: (props) => (
        <Cell {...props} value={props.value === null ? '-' : props.value} />
      ),
      accessor: 'pdv_perdus',
      name: I18n.t('restaurants&caves.kpi.columns.lostPov')
    },
    {
      Header: () => (
        <Header
          title={I18n.t('restaurants&caves.kpi.columns.wonPov')}
          sortable={true}
          sort={() => handleSort('pdv_gagnes')}
          tooltip={{
            placement: 'top',
            content: (
              <ContentTooltip values={[I18n.t('tableHeaderTooltips.wonPov')]} />
            )
          }}
        />
      ),
      Cell: (props) => (
        <Cell {...props} value={props.value === null ? '-' : props.value} />
      ),
      accessor: 'pdv_gagnes',
      name: I18n.t('restaurants&caves.kpi.columns.wonPov')
    },
    {
      Header: () => (
        <Header
          title={I18n.t('restaurants&caves.kpi.columns.tf')}
          sortable={true}
          sort={() => handleSort('tf')}
          tooltip={{
            placement: 'top',
            content: (
              <ContentTooltip values={[I18n.t('tableHeaderTooltips.tf')]} />
            )
          }}
        />
      ),
      Cell: (props) => <Cell {...props}>{props.value}</Cell>,
      sortMethod: (a, b) => {
        if (a && b) {
          a = Number(a.replace('%', ''));
          b = Number(b.replace('%', ''));
          return a - b;
        }
      },
      accessor: 'tf',
      name: I18n.t('restaurants&caves.kpi.columns.tf')
    },
    showDistributionKPIConsoRate(tags_names) && {
      Header: () => (
        <Header
          title={I18n.t('restaurants&caves.kpi.columns.tc')}
          sortable={true}
          sort={() => handleSort('tc')}
          tooltip={{
            placement: 'top',
            content: (
              <ContentTooltip values={[I18n.t('tableHeaderTooltips.tc')]} />
            )
          }}
        />
      ),
      Cell: (props) => (
        <Cell {...props} styles={`border-right: 1px solid #e8e9eb`}>
          {props.value}
        </Cell>
      ),
      sortMethod: (a, b) => {
        if (a && b) {
          a = Number(a.replace('%', ''));
          b = Number(b.replace('%', ''));
          return a - b;
        }
      },
      accessor: 'tc',
      name: I18n.t('restaurants&caves.kpi.columns.tc')
    },
    showDistributionKPIBestVintage(tags_names) && {
      Header: () => (
        <Header
          title={I18n.t('restaurants&caves.kpi.columns.bestVintage')}
          sortable={true}
          sort={() => handleSort('frequent_vintage')}
          tooltip={{
            placement: 'top',
            content: (
              <ContentTooltip
                values={[I18n.t('tableHeaderTooltips.bestVintage')]}
              />
            )
          }}
        />
      ),
      Cell: (props) => <Cell {...props}>{props.value}</Cell>,
      accessor: 'frequent_vintage',
      name: I18n.t('restaurants&caves.kpi.columns.bestVintage')
    },
    showDistributionKPIOldDN(tags_names) && {
      Header: () => (
        <Header
          title={I18n.t('restaurants&caves.kpi.columns.oldDn')}
          sortable={true}
          tooltip={{
            placement: 'top',
            content: (
              <ContentTooltip values={[I18n.t('tableHeaderTooltips.dnOld')]} />
            )
          }}
          sort={() => handleSort('dnOld')}
        />
      ),
      Cell: (props) => (
        <Cell
          {...props}
          styles={`border-right: 1px solid #e8e9eb`}
          value={props.value ? props.value.value : '-'}
          tooltip={
            props.value && props.value.tooltip
              ? {
                  placement: 'top',
                  content: <ContentTooltip values={[props.value.tooltip]} />
                }
              : undefined
          }
        />
      ),
      sortMethod: (a, b) =>
        Number(a && a.value ? a.value : 0) - Number(b && b.value ? b.value : 0),
      accessor: 'dnOld',
      name: I18n.t('restaurants&caves.kpi.columns.oldDn')
    },
    showDistributionKPDNBtg(tags_names) && {
      Header: () => (
        <Header
          title={I18n.t('restaurants&caves.kpi.columns.dn_btg')}
          sortable={true}
          sort={() => handleSort('dn_btg')}
          tooltip={{
            placement: 'top',
            content: (
              <ContentTooltip values={[I18n.t('tableHeaderTooltips.dnBtg')]} />
            )
          }}
        />
      ),
      Cell: (props) => <Cell {...props}>{props.value}</Cell>,
      sortMethod: (a, b) => Number(a) - Number(b),
      accessor: 'dn_btg',
      name: I18n.t('restaurants&caves.kpi.columns.dn_btg')
    },
    shouldDisplayBottleAvgPrice && {
      Header: () => (
        <Header
          title={I18n.t('restaurants&caves.kpi.columns.averagePrice')}
          sortable={true}
          sort={() => handleSort('pmc')}
          tooltip={{
            placement: 'top',
            content: (
              <ContentTooltip
                values={[I18n.t('tableHeaderTooltips.averagePrice')]}
              />
            )
          }}
        />
      ),
      Cell: (props) => (
        <Cell {...props} value={props.value === null ? '-' : props.value} />
      ),
      sortMethod: (a, b) =>
        parseInt(a.replace(/[^0-9-.]/g, '')) -
        parseInt(b.replace(/[^0-9-.]/g, '')),
      accessor: 'pmc',
      name: I18n.t('restaurants&caves.kpi.columns.averagePrice')
    },
    shouldDisplayBottleAvgPrice && {
      Header: () => (
        <Header
          title={I18n.t('restaurants&caves.kpi.columns.medianPrice')}
          sortable={true}
          sort={() => handleSort('median_price_formatted')}
          tooltip={{
            placement: 'top',
            content: (
              <ContentTooltip
                values={[I18n.t('tableHeaderTooltips.medianPrice')]}
              />
            )
          }}
        />
      ),
      Cell: (props) => (
        <Cell {...props} value={props.value === null ? '-' : props.value} />
      ),
      sortMethod: (a, b) =>
        parseInt(a.replace(/[^0-9-.]/g, '')) -
        parseInt(b.replace(/[^0-9-.]/g, '')),
      accessor: 'median_price_formatted',
      name: I18n.t('restaurants&caves.kpi.columns.medianPrice')
    },
    shouldDisplayGlassAvgPrice && {
      Header: () => (
        <Header
          title={I18n.t('restaurants&caves.kpi.columns.glassAveragePrice')}
          sortable={true}
          sort={() => handleSort('glass_avg_price')}
          tooltip={{
            placement: 'top',
            content: (
              <ContentTooltip
                values={[I18n.t('tableHeaderTooltips.glassAveragePrice')]}
              />
            )
          }}
        />
      ),
      Cell: (props) => (
        <Cell {...props} value={props.value === null ? '-' : props.value} />
      ),
      sortMethod: (a, b) =>
        parseInt(a.replace(/[^0-9-.]/g, '')) -
        parseInt(b.replace(/[^0-9-.]/g, '')),
      accessor: 'glass_avg_price',
      name: I18n.t('restaurants&caves.kpi.columns.glassAveragePrice')
    }
  ].filter((item) => item !== false);

  const visibleColumns = columns.filter(
    (col) => !hiddenColumns.includes(col.name)
  );

  return (
    <Table
      data={data}
      sorted={sortState.sorted}
      columns={visibleColumns}
      expanded={expanded}
    />
  );
};

export default KeyIndicators;
